/**
 * サーバーの鍵（sk_）の一覧の表（プロジェクトごとの見出し + 行）。2026-09-28 creator/dashboard-pass。
 * 使う所: pages/AccountSecretKeys.tsx（一覧・まとめて作り直すの確認）・pages/ResetDone.tsx（再設定の完了の「まだ有効な鍵」）。
 *
 * 鍵の値は無い（サーバーは displayPrefix だけを返す）。頭は keyHead で「種別タグ + 4 文字…」までに切る（画面共有で漏らさない・KeysPage と同じ）。
 * プロジェクト名・鍵の名前（label）は利用者（か、入り込んだ誰か）が付けた値 = 必ずテキストとして出す（{} で描く。HTML にしない）。
 * 狭い幅（640px 以下）では表をやめて 1 本ずつのカードにする（.table-stack・styles.css）。WHY: 5 列の表が 390px に収まらず、
 *   「アプリ」の列が「テス/ト用/のア/プリ」と 1〜2 文字ずつ縦に折れていた（2026-10-04 plain-words-4 の写真で発見・管理の判断で直す）。
 *   列の名前は data-label から ::before で出す（カードでも何の値か分かるように）。
 * 日時は相対（3 日前）ではなく日時そのものを出す: 「見覚えのない日時の鍵」を見分けてもらう画面なので、いつ作られたかが要る。
 */
import { Link } from 'react-router-dom';
import { keyHead } from '../api';
import { fullDate } from './ui';
import { useT, type MessageKey } from '../i18n';
import { groupByProject, keysPagePath, type SecretKeyView } from '../secretKeys';

export function SecretKeyTable({
  keys,
  linkToKeys = false,
  functionsMark = 'pill'
}: {
  keys: SecretKeyView[];
  /** 各プロジェクトの見出しに、その環境の API キーの画面へのリンクを出す（ログイン中の画面だけ） */
  linkToKeys?: boolean;
  /** Functions の自動の鍵の印: 'pill' = 「Functions」/ 'redeploy' = 「Functions を再デプロイ」（作り直すの確認の画面） */
  functionsMark?: 'pill' | 'redeploy';
}) {
  const { t } = useT();
  return (
    <div className="stack">
      {groupByProject(keys).map((g) => (
        <section key={g.projectId} className="sk-group">
          <h3 className="section-title sk-group-title">
            {/* プロジェクト名が空（掃除で全部落ちた等）のときは id の頭で区別できるようにする */}
            <span className="sk-text">{g.projectName || g.projectId.slice(0, 8)}</span>
          </h3>
          <div className="table-wrap">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th scope="col">{t('sk.col.key')}</th>
                  <th scope="col">{t('sk.col.env')}</th>
                  <th scope="col">{t('sk.col.via')}</th>
                  <th scope="col">{t('sk.col.created')}</th>
                  <th scope="col">{t('sk.col.lastUsed')}</th>
                  {linkToKeys && (
                    <th scope="col">
                      <span className="sr-only">{t('skList.openKeys')}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {g.items.map((k) => (
                  <tr key={k.id}>
                    <td data-label={t('sk.col.key')}>
                      <span className="mono">{keyHead(k.displayPrefix)}</span>
                      <span className="muted small sk-text"> · {k.label ?? t('sk.noLabel')}</span>
                      {k.functions && (
                        <>
                          {' '}
                          <span className={`pill ${functionsMark === 'redeploy' ? 'pill-warn' : ''}`}>
                            {t(functionsMark === 'redeploy' ? 'rotate.functionsRow' : 'sk.functionsPill')}
                          </span>
                        </>
                      )}
                    </td>
                    <td data-label={t('sk.col.env')}>{t(`env.inline.${k.environment}`)}</td>
                    <td data-label={t('sk.col.via')}>{t(`sk.via.${k.createdVia}` as MessageKey)}</td>
                    <td data-label={t('sk.col.created')}>{fullDate(k.createdAt)}</td>
                    <td data-label={t('sk.col.lastUsed')}>{k.lastUsedAt ? fullDate(k.lastUsedAt) : <span className="muted">{t('common.never')}</span>}</td>
                    {linkToKeys && (
                      <td className="cell-actions">
                        <Link to={keysPagePath(k.projectId, k.environment)} className="btn btn-ghost btn-sm">
                          {t('skList.openKeys')}
                        </Link>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
